import { Injectable } from '@angular/core';
import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DataRole } from '@mode-switch/express';
import { PivotPlan, RecordsPlan } from '@mode-switch/query';
import { QueryBuilder } from '@mode/charts/util-vegas';
import { ChartFacade } from '@mode/charts/contract';
import { EncodingHelpers, SwitchRenderModel, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { makePagingResult, isPresent } from '@mode/shared/util-js';
import { DataViewFacade, FlamingoActions, FlamingoFacade, SwitchAttrsActions } from '@mode/switch-views/contract';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, merge as observableMerge } from 'rxjs';
import { filter, first, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { reportFilterActions } from '..';
import { ReportViewerDomainState } from './report-viewer/report-viewer.selectors';

const adapter = new FlamingoAdapter();

const FLAMINGO_UNSUPPORTED_CHARTS = ['table', 'pivotTable'];

function isChart(chartType: ChartTypes.ChartType) {
  return !['bigValue', 'pivotTable', 'table'].includes(chartType);
}

@Injectable()
export class ReportChartViewerFacade {
  constructor(
    private store: Store<ReportViewerDomainState>,
    private actions$: Actions,
    private chartFacade: ChartFacade,
    private dataViewFacade: DataViewFacade,
    private flamingoFacade: FlamingoFacade
  ) {}

  executeVizPlanFromLayout(chartPlan: {
    plan: RecordsPlan<DataRole> | PivotPlan<DataRole>;
    chartToken: string;
    runToken: string;
  }) {
    return this.store.dispatch(
      FlamingoActions.executePlan({
        plan: chartPlan.plan,
        refId: chartPlan.chartToken,
        queryRunToken: chartPlan.runToken,
        allowRefresh: true,
      })
    );
  }

  createVizDataModel$(chartType: string, vizToken: string, queryToken: string, planId: string) {
    if (chartType === 'table') {
      // If chart type is table we need a plan result for count and records
      // in order to build the columns/data and paging/offset for the table UI
      // Note: we select the plan first in order to always get the latest
      // result data when a plan id change fires
      return this.flamingoFacade.createPlanFromPlanId(planId).pipe(
        switchMap((plan) =>
          combineLatest([
            this.flamingoFacade.createCountFromPlanId(planId),
            this.flamingoFacade.createRecordsFromPlanId(planId),
          ]).pipe(
            map(([count, records]) => {
              // To get the limit and offset for the record query
              // we are grabbing it from the plan
              // TODO: This should be reduced and selected from state directly
              const recordPlan = plan.queries.find((q) => q.name === 'records');
              if (records != null && recordPlan && count != null) {
                return {
                  columns: records?.columns,
                  content: records?.content,
                  paging: makePagingResult(
                    { offset: recordPlan.offset || 0, pageSize: recordPlan.limit || 100 },
                    count
                  ),
                };
              }
              return null;
            })
          )
        )
      );
    }
    // If this is a non table visualization, we just need the plan result
    // and data view model to generate visualization data
    // Note: we select the plan first in order to always get the latest
    // result data when a plan id change fires
    return this.flamingoFacade.createPlanFromPlanId(planId).pipe(
      switchMap((plan) =>
        combineLatest([this.flamingoFacade.createResultFromPlanId(planId), this.dataViewFacade.model$]).pipe(
          map(([result, model]) => {
            if (result != null && queryToken != null) {
              return SwitchRenderModel.make(model, queryToken, plan, result);
            }
            return null;
          })
        )
      )
    );
  }

  createReportViewChartPlan$(chartToken: string) {
    // Builds an observable for the given chart token that watches for any changes and
    // returns an ExecutePlan action for later dispatch.

    // GF and NON GF cases handled here. Everything downstream is equivalent
    const chart$ = this.createReportViewChart(chartToken);

    // Merge both chart/table observables together. Can't conflict since each one has an appropriate filter
    return observableMerge(
      chart$.pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            chartType === 'table' &&
            EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
            EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <= 0
        ),
        /* TODO: add report filters */
        switchMap(({ runToken, queryToken, encoding, model /*, filters */ }) =>
          this.chartFacade.createPagingOffsetFromVizToken(chartToken).pipe(
            first(),
            map((offset) => {
              // const filteredEncoding = this.addValidReportFiltersToEncoding(/*filters*/[], model, queryToken, encoding);
              return {
                runToken,
                chartToken,
                plan: QueryBuilder.buildRecordsPlan(
                  adapter,
                  model,
                  queryToken,
                  encoding /* filteredEncoding */,
                  100,
                  offset
                ),
              };
            })
          )
        )
      ),
      chart$.pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            FLAMINGO_UNSUPPORTED_CHARTS.indexOf(chartType) < 0 &&
            chartType !== 'table' &&
            EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <= 0
        ),
        /* TODO: Add report filters */
        filter(({ chartType, encoding }) => EncodingHelpers.isChartEncodingComplete(encoding, chartType)),
        map(({ runToken, queryToken, model, /*filters,*/ chartFormat, chartType, encoding }) => {
          // const filteredEncoding = this.addValidReportFiltersToEncoding(/*filters*/[], model, queryToken, encoding);
          const needsColorBackfill = isChart(chartType) && chartFormat?.colors.map == null;
          const includeSortDomain = chartType === 'vegas';
          return {
            runToken,
            chartToken,
            plan: QueryBuilder.buildPivotQuery(
              adapter,
              model,
              queryToken,
              encoding, // filteredEncoding,
              needsColorBackfill,
              includeSortDomain
            ),
          };
        })
      )
    );
  }

  /**
   * Create the chart$ that gets used in the plans observable.
   * Listens to three sources for data required to request a helix visualizations data
   * 1. chart facade for up to date and valid chart encoding
   * 2. data view facade for switch view data
   * 3. report filter facade for additional report level filters
   */
  private createReportViewChart(chartToken: string) {
    return this.chartFacade.createChartTypeEncodingFromVizToken(chartToken).pipe(
      filter(isPresent),
      filter(({ chartType, encoding }) => EncodingHelpers.isChartEncodingComplete(encoding, chartType)),
      switchMap(({ queryToken, chartType, encoding }) => {
        return this.dataViewFacade.createRunReadyByQueryToken(queryToken).pipe(
          filter((ready) => ready == true && encoding != null),
          switchMap(() => this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(queryToken).pipe(first())),
          switchMap(
            () =>
              combineLatest(
                this.actions$.pipe(
                  // respond to create filter action so it gets options filled in
                  ofType(
                    reportFilterActions.applyReportFilters,
                    reportFilterActions.updateReportFilterDefaultsSuccess,
                    reportFilterActions.destroyReportFilterSuccess
                  ),
                  startWith(null)
                ),
                /* TODO: Respond to Chart Action to update Calc */
                this.actions$.pipe(
                  // On update calculation, rerun this
                  ofType(SwitchAttrsActions.updateSwitchViewAttrSuccess),
                  filter((a) => a.queryToken === queryToken),
                  startWith(null)
                )
              ),
            (runToken) => runToken // Drop action out of stream
          ),
          withLatestFrom(this.dataViewFacade.model$, this.chartFacade.createChartFormatFromVizToken(chartToken)),
          map(([runToken, model, chartFormat]) => ({
            queryToken,
            model,
            runToken,
            chartFormat,
            chartToken,
            chartType,
            encoding,
          }))
        );
      })
    );
  }
}
